/**
 * Where each AffDice SuperAdmin screen lives in the address bar.
 *
 * The menu has six headings — Overview, Operators, Brands, Affiliates,
 * Platform, Integrity — and the last two hold several screens each, so a
 * screen's address names its heading too: `#platform/users`,
 * `#integrity/audit`. The id is the screen's own name and stays flat, because
 * the shell switches on it and a check reads that switch.
 *
 * Kept free of React so a test can read it without a DOM.
 */

export const ROUTE_IDS = [
  'overview',
  'operators',
  'brands',
  'affiliates',
  'users',
  'roles',
  'reference-data',
  'feature-flags',
  'plans',
  'audit',
  'consumers',
  'access-log',
  'login-as',
] as const;

export type RouteId = (typeof ROUTE_IDS)[number];

/** The hash each screen is reached at, without the leading `#`. */
export const ROUTE_PATHS: Readonly<Record<RouteId, string>> = {
  overview: 'overview',
  operators: 'operators',
  brands: 'brands',
  affiliates: 'affiliates',
  users: 'platform/users',
  roles: 'platform/roles',
  'reference-data': 'platform/reference-data',
  'feature-flags': 'platform/feature-flags',
  plans: 'platform/plans',
  audit: 'integrity/audit',
  consumers: 'integrity/consumers',
  'access-log': 'integrity/access-log',
  'login-as': 'integrity/login-as',
};

/**
 * Addresses from before the menu was regrouped. A bookmark or a link pasted
 * into a ticket still lands where it used to: `#dashboard` was the Overview,
 * and `#staff` was always the Users screen under another name.
 */
const LEGACY: Readonly<Record<string, RouteId>> = {
  dashboard: 'overview',
  staff: 'users',
  audit: 'audit',
  consumers: 'consumers',
  'reference-data': 'reference-data',
  'feature-flags': 'feature-flags',
};

export const DEFAULT_ROUTE: RouteId = 'overview';

/** The screen a hash names, or the Overview for one that names nothing. */
export function routeFromHash(hash: string): RouteId {
  const raw = hash.replace(/^#\/?/, '').replace(/\/+$/, '');
  for (const id of ROUTE_IDS) if (ROUTE_PATHS[id] === raw) return id;
  return LEGACY[raw] ?? DEFAULT_ROUTE;
}

/** The hash, with its `#`, that opens a screen. */
export function hashFor(id: RouteId): string {
  return `#${ROUTE_PATHS[id]}`;
}
